import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Psychology as AIIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { aiGovernanceAPI } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { UI_TOKENS } from '../../constants/uiTokens';
import { BTN_SM, CARD_SX, PAGE } from '../../constants/pageStyles';
import { getApiErrorMessage } from '../../utils/apiErrors';
import type { AIGovernanceDashboardMetrics } from '../../types/aiGovernance';

interface MetricCardProps {
  label: string;
  value: number;
  color: string;
  icon: React.ReactNode;
  onClick?: () => void;
  subtitle?: string;
}

function MetricCard({ label, value, color, icon, onClick, subtitle }: MetricCardProps) {
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        ...CARD_SX,
        p: 2,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s',
        '&:hover': onClick ? { boxShadow: 2 } : undefined,
        borderTop: `3px solid ${color}`,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${color}18`,
            color,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: UI_TOKENS.text.labelXs, color: 'text.secondary', fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.2 }}>{value}</Typography>
          {subtitle && (
            <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary', mt: 0.25 }} noWrap>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

interface Props {
  refreshToken?: number;
  onNavigateTab?: (tab: 'inventory' | 'access' | 'settings') => void;
}

const AiGovernanceDashboardPanel: React.FC<Props> = ({ refreshToken = 0, onNavigateTab }) => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<AIGovernanceDashboardMetrics | null>(null);
  const [exporting, setExporting] = useState<'assets' | 'grants' | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await aiGovernanceAPI.getDashboardMetrics();
      setMetrics(res.data.data ?? null);
    } catch (e: unknown) {
      showError(getApiErrorMessage(e, 'Failed to load dashboard metrics'));
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  const downloadExport = async (type: 'assets' | 'grants') => {
    setExporting(type);
    try {
      const res = await aiGovernanceAPI.exportCsv(type);
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = type === 'assets' ? `ai-assets-${new Date().toISOString().slice(0, 10)}.csv` : `ai-access-grants-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showSuccess(`${type === 'assets' ? 'Asset' : 'Grant'} export downloaded`);
    } catch (e: unknown) {
      showError(getApiErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!metrics) {
    return (
      <Typography sx={{ color: 'text.secondary', fontSize: UI_TOKENS.text.bodySm }}>
        Dashboard metrics are unavailable.
      </Typography>
    );
  }

  const activeCount = metrics.assets.by_status.Active ?? 0;
  const draftCount = metrics.assets.by_status.Draft ?? 0;
  const statusParts = Object.entries(metrics.assets.by_status)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([status, count]) => `${count} ${status.toLowerCase()}`);
  const listed = Object.values(metrics.assets.by_status).reduce((sum, count) => sum + count, 0);
  const unlisted = Math.max(0, metrics.assets.total - listed);
  if (unlisted > 0) statusParts.push(`${unlisted} other`);
  const assetStatusLine = statusParts.length
    ? `${statusParts.join(' · ')} · ${metrics.assets.total} together`
    : `${metrics.assets.total} together`;
  const highRisk =
    (metrics.assets.by_risk_tier.High ?? 0) +
    (metrics.assets.by_risk_tier.Critical ?? 0);

  const pendingTotal =
    metrics.access.pending_requests + metrics.access.pending_security_review;

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ ...CARD_SX, p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1.5}>
          <Box>
            <Typography sx={PAGE.sectionTitle}>Overview</Typography>
            <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: 'text.secondary' }}>
              Scoped to assets and grants you can view. Click a tile to jump to the relevant section.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={exporting === 'assets' ? <CircularProgress size={14} /> : <DownloadIcon />}
              onClick={() => void downloadExport('assets')}
              disabled={Boolean(exporting)}
              sx={BTN_SM}
            >
              Export assets
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={exporting === 'grants' ? <CircularProgress size={14} /> : <DownloadIcon />}
              onClick={() => void downloadExport('grants')}
              disabled={Boolean(exporting)}
              sx={BTN_SM}
            >
              Export grants
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => navigate('/audit-trail?entity_type=ai_asset')}
              sx={BTN_SM}
            >
              AI audit log
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Typography sx={{ ...PAGE.sectionTitle, mb: -0.5 }}>Assets</Typography>
      <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: 'text.secondary', mt: -0.5 }}>
        {assetStatusLine}
      </Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Total assets"
            value={metrics.assets.total}
            color="#1565c0"
            icon={<AIIcon sx={{ fontSize: 20 }} />}
            onClick={() => onNavigateTab?.('inventory')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Active"
            value={activeCount}
            color="#2e7d32"
            icon={<CheckCircleIcon sx={{ fontSize: 20 }} />}
            subtitle={`${draftCount} draft`}
            onClick={() => onNavigateTab?.('inventory')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="High / critical risk"
            value={highRisk}
            color="#c62828"
            icon={<SecurityIcon sx={{ fontSize: 20 }} />}
            onClick={() => onNavigateTab?.('inventory')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Material changes pending"
            value={metrics.assets.material_changes_pending}
            color="#ef6c00"
            icon={<WarningIcon sx={{ fontSize: 20 }} />}
            onClick={() => onNavigateTab?.('inventory')}
          />
        </Grid>
      </Grid>

      <Typography sx={{ ...PAGE.sectionTitle, mb: -0.5, mt: 1 }}>Access & lifecycle</Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Pending approvals"
            value={pendingTotal}
            color="#6a1b9a"
            icon={<AssignmentIcon sx={{ fontSize: 20 }} />}
            subtitle={
              metrics.access.pending_security_review > 0
                ? `${metrics.access.pending_security_review} security review`
                : undefined
            }
            onClick={() => onNavigateTab?.('access')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Active grants"
            value={metrics.access.active_grants}
            color="#00838f"
            icon={<CheckCircleIcon sx={{ fontSize: 20 }} />}
            onClick={() => onNavigateTab?.('access')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Expiring soon"
            value={metrics.access.expiring_soon}
            color="#f57f17"
            icon={<ScheduleIcon sx={{ fontSize: 20 }} />}
            onClick={() => onNavigateTab?.('access')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Recertification due"
            value={metrics.access.pending_recertification}
            color="#ad1457"
            icon={<WarningIcon sx={{ fontSize: 20 }} />}
            onClick={() => onNavigateTab?.('access')}
          />
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ ...CARD_SX, p: 2, bgcolor: 'grey.50' }}>
        <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: 'text.secondary' }}>
          Scheduler: {metrics.scheduler.enabled ? 'enabled' : 'disabled'}
          {metrics.scheduler.enabled && ` · runs every ${metrics.scheduler.interval_minutes} minutes`}
          {metrics.scheduler.last_run_at &&
            ` · last run ${new Date(metrics.scheduler.last_run_at).toLocaleString()}`}
          {` · ${metrics.access.expired_grants} expired grant${metrics.access.expired_grants === 1 ? '' : 's'}`}
        </Typography>
      </Paper>
    </Stack>
  );
};

export default AiGovernanceDashboardPanel;
